import {useEffect, useRef} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {useIncomingCall} from '../hooks/useIncomingCall'
import {useAWSConnect} from '../app/context/ConnectStreamsContext'

const WARNING_TITLE = '\u26A0 About to go offline'
const BEEP_INTERVAL_MS = 3000
const TITLE_FLASH_INTERVAL_MS = 1000
const HEARTBEAT_INTERVAL_MS = 30000
const STALL_THRESHOLD_MS = 240000

export const OfflineWarningModal = () => {
  const {showOfflineWarning, triggerOfflineWarning, dismissOfflineWarning, activeCalls, showModal, isAcceptedAndConnecting} =
    useIncomingCall()
  const {isLoggedIn} = useAWSConnect()
  const audioContextRef = useRef<AudioContext | null>(null)
  const lastTickRef = useRef<number>(Date.now())
  const showOfflineWarningRef = useRef(showOfflineWarning)
  const canWarnRef = useRef(false)

  const isOnCall = activeCalls.length > 0 || showModal || isAcceptedAndConnecting
  const canWarn = isLoggedIn && !isOnCall

  useEffect(() => {
    showOfflineWarningRef.current = showOfflineWarning
  }, [showOfflineWarning])

  useEffect(() => {
    canWarnRef.current = canWarn
  }, [canWarn])

  useEffect(() => {
    if (isOnCall && showOfflineWarning) {
      dismissOfflineWarning()
    }
  }, [isOnCall, showOfflineWarning, dismissOfflineWarning])

  useEffect(() => {
    lastTickRef.current = Date.now()

    const heartbeat = setInterval(() => {
      const now = Date.now()
      const gap = now - lastTickRef.current
      lastTickRef.current = now
      if (gap > STALL_THRESHOLD_MS && !showOfflineWarningRef.current && canWarnRef.current) {
        triggerOfflineWarning()
      }
    }, HEARTBEAT_INTERVAL_MS)

    return () => clearInterval(heartbeat)
  }, [triggerOfflineWarning])

  useEffect(() => {
    if (!showOfflineWarning || !canWarn) return

    const playBeep = () => {
      try {
        const AudioContextClass =
          window.AudioContext || (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
        if (!AudioContextClass) return

        const ctx = audioContextRef.current ?? new AudioContextClass()
        audioContextRef.current = ctx
        if (ctx.state === 'suspended') ctx.resume().catch(() => {})

        const oscillator = ctx.createOscillator()
        const gain = ctx.createGain()
        oscillator.connect(gain)
        gain.connect(ctx.destination)
        oscillator.type = 'sine'
        oscillator.frequency.value = 880
        gain.gain.setValueAtTime(0.2, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.4)
      } catch {
        audioContextRef.current = null
      }
    }

    playBeep()
    const beepInterval = setInterval(playBeep, BEEP_INTERVAL_MS)

    const originalTitle = document.title
    let toggled = false
    const titleInterval = setInterval(() => {
      document.title = toggled ? originalTitle : WARNING_TITLE
      toggled = !toggled
    }, TITLE_FLASH_INTERVAL_MS)

    return () => {
      clearInterval(beepInterval)
      clearInterval(titleInterval)
      document.title = originalTitle
    }
  }, [showOfflineWarning, canWarn])

  return (
    <Modal show={showOfflineWarning && canWarn} onHide={dismissOfflineWarning} centered backdrop="static">
      <Modal.Body className="text-center p-5">
        <h4 className="fw-bold text-danger mb-3">You're about to go offline</h4>
        <p className="text-muted mb-4">
          We couldn't confirm you're still here. Click below to stay available and keep receiving calls.
        </p>
        <Button
          variant="danger"
          className="w-100 rounded-pill fw-bold py-3"
          style={{backgroundColor: '#D7383C', border: 'none'}}
          onClick={dismissOfflineWarning}
        >
          I'm here — keep me available
        </Button>
      </Modal.Body>
    </Modal>
  )
}

export default OfflineWarningModal
